import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck, CalendarRange, CheckCircle2, Flame, Layers, ListTodo, Mail, MoreHorizontal,
  Phone, Plus, Search, Sparkles, Target, TrendingUp, Trophy, UserPlus, UserRound,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RdvModal from "@/components/RdvModal";
import { toast } from "sonner";
import { fmtEuro } from "@/lib/constants";
import ProspectModal from "@/components/prospects/ProspectModal";
import ProspectPanel from "@/components/prospects/ProspectPanel";
import AgentIaPanel from "@/components/prospects/AgentIaPanel";
import { PIPELINE, statusOf, SOURCES, LOSS_REASONS, isActive, isOverdue, frDate, sourceLabel, actionLabel } from "@/components/prospects/constants";

const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const AVATAR_BG = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#FEF3C7] text-[#B45309]", "bg-[#EDE9FE] text-[#6D28D9]", "bg-[#DCFCE7] text-[#15803D]", "bg-[#FCE7F3] text-[#BE185D]"];
const avatarCls = (name = "") => AVATAR_BG[(name.charCodeAt(0) || 0) % AVATAR_BG.length];
const valOf = (p) => (p.value_kind === "recurrent" ? (p.value_monthly || 0) * 12 : (p.value_amount || 0)) || 0;
const valLabel = (p) => (p.value_kind === "recurrent" && p.value_monthly ? `${fmtEuro(p.value_monthly)}/mois` : p.value_amount ? fmtEuro(p.value_amount) : null);
const ACTION_ICON = { appel: Phone, email: Mail, rdv: CalendarCheck, relance: Mail, devis: FileText2, autre: ListTodo };
const PRIO_DOT = { prioritaire: "text-violet-600", haute: "text-rose-500", moyenne: "text-amber-500", basse: "text-neutral-400" };

const KANBAN = [
  { id: "prospect", label: "Prospect", statuses: ["nouveau", "a_contacter"], target: "a_contacter", head: "bg-neutral-50/90 border-neutral-200" },
  { id: "contacte", label: "Contacté", statuses: ["contacte", "a_relancer"], target: "contacte", head: "bg-blue-50/80 border-blue-100" },
  { id: "rdv_fixe", label: "RDV fixé", statuses: ["rdv_fixe"], target: "rdv_fixe", head: "bg-violet-50/80 border-violet-100" },
  { id: "devis_envoye", label: "Proposition envoyée", statuses: ["devis_envoye"], target: "devis_envoye", head: "bg-amber-50/80 border-amber-100" },
  { id: "negociation", label: "Négociation", statuses: ["negociation"], target: "negociation", head: "bg-rose-50/80 border-rose-100" },
  { id: "gagne", label: "Gagné", statuses: ["gagne"], target: "gagne", head: "bg-emerald-50/80 border-emerald-100" },
];
const PERDU_COL = { id: "perdu", label: "Perdu", statuses: ["perdu"], target: "perdu", head: "bg-neutral-100 border-neutral-200" };
const colOf = (p) => (KANBAN.find((c) => c.statuses.includes(p.status)) || (p.status === "perdu" ? PERDU_COL : KANBAN[0])).id;

function FileText2(props) { return <ListTodo {...props} />; }

function LossDialog({ prospect, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="loss-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">Motif de perte — {prospect.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <select data-testid="loss-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white">
            <option value="">Sélectionnez un motif…</option>
            {LOSS_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="text-[11px] text-neutral-400">Le motif est conservé pour les statistiques commerciales.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button data-testid="loss-confirm" disabled={!reason} onClick={() => onConfirm(reason)}
              className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white">Marquer perdu</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewClientDialog({ onClose }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", contact_name: "", email: "", phone: "" });
  const [dups, setDups] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async (force = false) => {
    if (!f.name.trim()) { toast.error("Nom de l'entreprise requis"); return; }
    setSaving(true);
    try {
      if (!force) {
        const { data } = await api.post("/prospects/check-duplicate", { email: f.email, phone: f.phone, name: f.name });
        if (data.duplicates.length) { setDups(data.duplicates); setSaving(false); return; }
      }
      const { data } = await api.post("/clients", {
        name: f.name.trim(), email: f.email.trim(),
        contact_name: f.contact_name.trim(), phone: f.phone.trim(),
      });
      toast.success("Client créé — visible dans la page Clients");
      window.dispatchEvent(new Event("sf:clients"));
      onClose();
      navigate(`/clients/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="new-client-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">Nouveau client</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Input data-testid="new-client-name" placeholder="Entreprise *" value={f.name} onChange={set("name")} /></div>
          <div><Input data-testid="new-client-contact" placeholder="Contact (nom / prénom)" value={f.contact_name} onChange={set("contact_name")} /></div>
          <div><Input data-testid="new-client-email" type="email" placeholder="Email" value={f.email} onChange={set("email")} /></div>
          <div><Input data-testid="new-client-phone" placeholder="Téléphone" value={f.phone} onChange={set("phone")} /></div>
          {dups && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-1.5" data-testid="new-client-dup-alert">
              <p className="text-xs font-medium text-amber-800">Doublon possible :</p>
              {dups.map((d) => (
                <p key={d.id} className="text-xs text-amber-700">• {d.name} ({d.type}, via {d.via})</p>
              ))}
              <button onClick={() => save(true)} data-testid="new-client-dup-force"
                className="text-xs font-medium text-amber-800 underline">Créer quand même</button>
            </div>
          )}
          <Button data-testid="new-client-save" onClick={() => save(false)} disabled={saving}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {saving ? "Création…" : "Créer la fiche client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProspectCard({ p, onOpen, onEdit, onLost, onDelete }) {
  const [menu, setMenu] = useState(false);
  const overdue = isOverdue(p);
  const ActionIcon = ACTION_ICON[p.next_action?.type] || ListTodo;
  return (
    <div draggable data-testid={`prospect-card-${p.id}`}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", p.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={() => onOpen(p.id)}
      className={`bg-white border rounded-xl p-2.5 cursor-pointer transition-all hover:shadow-sm group ${
        overdue ? "border-red-300" : "border-neutral-200/80 hover:border-neutral-300"}`}>
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ${avatarCls(p.name)}`}>{initialsOf(p.name)}</span>
        <span className="flex-1 min-w-0 text-xs font-bold text-[#0F172A] truncate">{p.name}</span>
        {p.ai && (
          <span className={`text-[9px] font-bold shrink-0 ${PRIO_DOT[p.ai.priorite] || "text-neutral-400"}`} title={p.ai.resume} data-testid={`prospect-ai-${p.id}`}>
            ✨ {p.ai.score}
          </span>
        )}
        <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setMenu((v) => !v)} data-testid={`prospect-menu-${p.id}`}
            className="p-0.5 rounded text-neutral-300 hover:text-neutral-600 hover:bg-neutral-100 opacity-0 group-hover:opacity-100 transition-all">
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-5 z-40 w-44 bg-white border border-neutral-200 rounded-xl shadow-lg py-1">
                <button onClick={() => { setMenu(false); onOpen(p.id); }} data-testid={`prospect-open-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-neutral-50">
                  <UserRound className="w-3 h-3 text-neutral-400" /> Ouvrir la fiche
                </button>
                <button onClick={async () => { setMenu(false); try { await api.post("/tasks", { title: `Relancer ${p.company || p.name}`, service: "Prospection", deadline: new Date(Date.now() + 86400000).toISOString().slice(0, 10), source_type: "prospect", source_id: p.id, source_label: `Prospection — ${p.company || p.name}`, source_url: "/prospection" }); toast.success("Tâche ajoutée à la To-do"); window.dispatchEvent(new Event("sf:tasks")); } catch { toast.error("Création impossible"); } }} data-testid={`prospect-task-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-neutral-50">
                  <ListTodo className="w-3 h-3 text-neutral-400" /> Ajouter une tâche
                </button>
                <button onClick={() => { setMenu(false); onEdit(p); }} data-testid={`prospect-edit-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-neutral-50">
                  <Sparkles className="w-3 h-3 text-neutral-400" /> Modifier
                </button>
                <button onClick={() => { setMenu(false); onLost(p); }} data-testid={`prospect-lost-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left text-orange-600 hover:bg-orange-50">
                  <CheckCircle2 className="w-3 h-3" /> Marquer perdu
                </button>
                <button onClick={() => { setMenu(false); onDelete(p); }} data-testid={`prospect-delete-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left text-red-600 hover:bg-red-50">
                  <CheckCircle2 className="w-3 h-3" /> Supprimer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <p className="text-[10px] text-neutral-400 mt-1 truncate">{[p.contact_name, p.sector].filter(Boolean).join(" · ") || "—"}</p>
      {valLabel(p) && <p className="text-xs font-bold text-[#0F172A] mt-1">{valLabel(p)}</p>}
      {p.ai?.prochaine_action && (
        <p className="text-[10px] font-semibold text-violet-600 mt-1 truncate" data-testid={`prospect-ai-reco-${p.id}`}>
          ✨ {p.ai.prochaine_action}
        </p>
      )}
      <div className="flex items-center justify-between mt-1.5">
        {p.next_action?.date ? (
          <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${overdue ? "text-red-600" : "text-neutral-500"}`} data-testid={`prospect-action-${p.id}`}>
            <ActionIcon className="w-3 h-3" /> {actionLabel(p.next_action.type)}
            {p.next_action.note ? ` — ${p.next_action.note.slice(0, 24)}` : ""}
          </span>
        ) : <span />}
        <span className={`text-[9px] ${overdue ? "text-red-500 font-semibold" : "text-neutral-300"}`}>{frDate(p.next_action?.date || p.created_at?.slice(0, 10))}</span>
      </div>
    </div>
  );
}

export default function Prospects() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [newClient, setNewClient] = useState(false);
  const [rdvFor, setRdvFor] = useState(null);
  const [lossFor, setLossFor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const [search, setSearch] = useState("");
  const [respFilter, setRespFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [aiFilter, setAiFilter] = useState("");
  const [showPerdu, setShowPerdu] = useState(false);
  const [view, setView] = useState("kanban");

  const load = () => {
    api.get("/prospects").then((r) => setProspects(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/appointments").then((r) => setAppointments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const applyStatus = async (p, status, loss_reason) => {
    try {
      await api.patch(`/prospects/${p.id}`, { status, ...(loss_reason ? { loss_reason } : {}) });
      toast.success(`Étape mise à jour — ${statusOf(status).label}`, {
        action: !loss_reason ? { label: "Annuler", onClick: async () => {
          await api.patch(`/prospects/${p.id}`, { status: p.status }).catch(() => {});
          load();
        } } : undefined,
      });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const moveProspect = (p, col) => {
    const target = col.target;
    if (p.status === target || colOf(p) === col.id) return;
    if (target === "rdv_fixe") { setRdvFor(p); return; }
    if (target === "perdu") { setLossFor(p); return; }
    setProspects((arr) => arr.map((x) => (x.id === p.id ? { ...x, status: target } : x)));
    applyStatus(p, target);
  };

  const deleteProspect = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.name} » ?`)) return;
    try {
      await api.delete(`/prospects/${p.id}`);
      toast.success("Prospect supprimé");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const completeTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "finalise" ? "a_faire" : "finalise" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const dateLabelRaw = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const dateLabel = dateLabelRaw.charAt(0).toUpperCase() + dateLabelRaw.slice(1);

  const filtered = useMemo(() => prospects.filter((p) => {
    if (!showPerdu && p.status === "perdu") return false;
    if (respFilter && (p.responsible || "") !== respFilter) return false;
    if (tagFilter && !(p.tags || []).includes(tagFilter)) return false;
    if (aiFilter === "avec" && !p.ai) return false;
    if (aiFilter === "sans" && p.ai) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [p.name, p.contact_name, p.email, p.phone, p.sector, p.service].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [prospects, search, respFilter, tagFilter, aiFilter, showPerdu]);

  /* KPI réels */
  const kpi = useMemo(() => {
    const month = today.slice(0, 7);
    const prev = new Date(); prev.setMonth(prev.getMonth() - 1);
    const prevMonth = prev.toISOString().slice(0, 7);
    const newMonth = prospects.filter((p) => (p.created_at || "").startsWith(month)).length;
    const newPrev = prospects.filter((p) => (p.created_at || "").startsWith(prevMonth)).length;
    const actifs = prospects.filter((p) => isActive(p));
    const gagnes = prospects.filter((p) => p.status === "gagne" || p.converted_client_id).length;
    const perdus = prospects.filter((p) => p.status === "perdu").length;
    const pipeline = actifs.reduce((a, p) => a + valOf(p), 0);
    const weighted = actifs.reduce((a, p) => a + valOf(p) * (p.probability || 0) / 100, 0);
    return {
      newMonth, varNew: newPrev > 0 ? Math.round((newMonth - newPrev) / newPrev * 100) : null,
      actifs: actifs.length,
      conv: (gagnes + perdus) > 0 ? Math.round(gagnes / (gagnes + perdus) * 100) : 0,
      pipeline, weighted,
    };
  }, [prospects, today]);

  const responsables = useMemo(() => [...new Set(prospects.map((p) => p.responsible).filter(Boolean))], [prospects]);
  const allTags = useMemo(() => [...new Set(prospects.flatMap((p) => p.tags || []))], [prospects]);

  const suivisJour = useMemo(() => tasks
    .filter((t) => t.prospect_id && t.deadline && t.deadline <= today && ["a_faire", "en_cours", "finalise"].includes(t.status))
    .sort((a, b) => (a.status === "finalise") - (b.status === "finalise"))
    .slice(0, 8), [tasks, today]);

  const prochainsRdv = useMemo(() => appointments
    .filter((a) => a.prospect_id && a.date >= today)
    .sort((a, b) => (a.date + String(a.start_min ?? 0).padStart(4, "0")).localeCompare(b.date + String(b.start_min ?? 0).padStart(4, "0")))
    .slice(0, 4), [appointments, today]);

  const recents = useMemo(() => [...prospects]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 4), [prospects]);

  const cols = showPerdu ? [...KANBAN, PERDU_COL] : KANBAN;
  const byCol = useMemo(() => Object.fromEntries(cols.map((c) => {
    const list = filtered.filter((p) => c.statuses.includes(p.status));
    return [c.id, { list, total: list.reduce((a, p) => a + valOf(p), 0) }];
  })), [filtered, showPerdu]); // eslint-disable-line

  const selected = prospects.find((p) => p.id === selectedId) || null;
  const kpis = [
    { id: "nouveaux", label: "Nouveaux prospects", icon: UserPlus, bg: "#E8F0FE", fg: "#1D4ED8", value: kpi.newMonth, sub: kpi.varNew != null ? `↑ ${kpi.varNew >= 0 ? "+" : ""}${kpi.varNew} %` : "—", subLbl: "vs mois précédent", subCls: kpi.varNew != null && kpi.varNew < 0 ? "text-rose-500" : "text-emerald-600" },
    { id: "opportunites", label: "Opportunités en cours", icon: Layers, bg: "#FEF3E2", fg: "#B45309", value: kpi.actifs, sub: `${prospects.length} au total`, subLbl: "", subCls: "text-neutral-400" },
    { id: "conversion", label: "Taux de conversion", icon: Trophy, bg: "#EDE9FE", fg: "#6D28D9", value: `${kpi.conv} %`, sub: "gagnés / (gagnés + perdus)", subLbl: "", subCls: "text-neutral-400" },
    { id: "pipeline", label: "Pipeline estimé", icon: TrendingUp, bg: "#DCFCE7", fg: "#15803D", value: fmtEuro(kpi.pipeline), sub: kpi.weighted > 0 ? `≈ ${fmtEuro(kpi.weighted)} pondéré` : "valeurs déclarées", subLbl: "", subCls: "text-emerald-600" },
  ];

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-8rem)] overflow-hidden" data-testid="prospects-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">Prospection <Target className="w-5 h-5 text-[#1D4ED8]" /></h1>
          <p className="text-neutral-500 text-xs mt-0.5">{dateLabel} — Suivez et gérez votre pipeline commercial de manière simple et efficace.</p>
        </div>
        <div className="flex items-center gap-2">
          <p className="hidden 2xl:block text-right text-xs italic text-neutral-400 leading-snug mr-2">« Des prospects d'aujourd'hui,<br />aux belles collaborations de demain. »</p>
          <Button size="sm" variant="outline" onClick={() => setAgentOpen(true)} className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="agent-ia-btn">
            <Sparkles className="w-3.5 h-3.5" /> Agent IA
          </Button>
          <Button size="sm" variant="outline" onClick={() => setNewClient(true)} className="gap-1.5" data-testid="new-client-btn">
            <UserPlus className="w-3.5 h-3.5" /> Nouveau client
          </Button>
          <Button size="sm" onClick={() => setCreating(true)} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5" data-testid="prospect-new">
            <Plus className="w-3.5 h-3.5" /> Ajouter un prospect
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0" data-testid="prospects-kpis">
        {kpis.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid={`pkpi-${c.id}`}>
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: c.bg }}>
                <c.icon className="w-4.5 h-4.5" style={{ color: c.fg }} />
              </span>
              <p className="text-[11px] font-medium text-neutral-500 leading-tight">{c.label}</p>
            </div>
            <p className="font-heading text-2xl font-bold mt-1.5 leading-tight" data-testid={`pkpi-${c.id}-value`}>{c.value}</p>
            <p className="text-[10px] mt-0.5"><span className={`font-semibold ${c.subCls}`}>{c.sub}</span> <span className="text-neutral-400">{c.subLbl}</span></p>
          </div>
        ))}
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_310px] gap-3">
        {/* Pipeline */}
        <div className="min-h-0 bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3 pb-2 shrink-0 flex-wrap" data-testid="prospects-filters">
            <CalendarRange className="w-4 h-4 text-neutral-400 shrink-0" />
            <h2 className="font-heading font-bold text-[15px] mr-1">Pipeline commercial</h2>
            <div className="relative w-44">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input className="pl-8 h-7.5 text-xs rounded-lg py-1.5" placeholder="Rechercher…" value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="prospects-search" />
            </div>
            <select value={respFilter} onChange={(e) => setRespFilter(e.target.value)} data-testid="prospects-filter-resp"
              className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none">
              <option value="">Tous les responsables</option>
              {responsables.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} data-testid="prospects-filter-tag"
              className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none">
              <option value="">Tous les tags</option>
              {allTags.map((t) => <option key={t} value={t}>#{t}</option>)}
            </select>
            <select value={aiFilter} onChange={(e) => setAiFilter(e.target.value)} data-testid="prospects-filter-ai"
              className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none">
              <option value="">IA + manuel</option>
              <option value="avec">Avec reco IA</option>
              <option value="sans">Sans reco IA</option>
            </select>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={() => setShowPerdu((v) => !v)} data-testid="prospects-toggle-perdu"
                className={`text-[10px] font-semibold border rounded-full px-2.5 py-1 transition-colors ${showPerdu ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-200 text-neutral-400"}`}>
                Perdus
              </button>
              <div className="flex border border-neutral-200 rounded-lg overflow-hidden" data-testid="prospects-view">
                <button onClick={() => setView("kanban")} data-testid="view-kanban"
                  className={`text-[10px] font-semibold px-2.5 py-1.5 transition-colors ${view === "kanban" ? "bg-[#0B132B] text-white" : "text-neutral-500"}`}>
                  Vue Kanban
                </button>
                <button onClick={() => setView("liste")} data-testid="view-liste"
                  className={`text-[10px] font-semibold px-2.5 py-1.5 transition-colors ${view === "liste" ? "bg-[#0B132B] text-white" : "text-neutral-500"}`}>
                  Vue liste
                </button>
              </div>
            </div>
          </div>

          {prospects.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center" data-testid="prospects-empty">
              <Target className="w-8 h-8 text-neutral-300 mb-3" />
              <p className="font-heading font-bold">Aucun prospect pour le moment</p>
              <p className="text-sm text-neutral-500 mt-1">Ajoutez vos premiers prospects et laissez l'IA rédiger vos messages de prospection.</p>
            </div>
          ) : view === "kanban" ? (
            <div className="flex-1 min-h-0 overflow-x-auto px-3 pb-3">
              <div className="h-full min-h-0 flex gap-2.5">
                {cols.map((col) => (
                  <div key={col.id}
                    onDragOver={(e) => { e.preventDefault(); setDragCol(col.id); }}
                    onDragLeave={() => setDragCol((c) => (c === col.id ? null : c))}
                    onDrop={(e) => {
                      e.preventDefault(); setDragCol(null);
                      const pid = e.dataTransfer.getData("text/plain");
                      const p = prospects.find((x) => x.id === pid);
                      if (p) moveProspect(p, col);
                    }}
                    className={`flex flex-col min-h-0 rounded-2xl border w-[215px] shrink-0 transition-shadow ${col.head} ${dragCol === col.id ? "ring-2 ring-[#93C5FD]" : ""}`}
                    data-testid={`prospects-col-${col.id}`}>
                    <div className="px-3 pt-2.5 pb-1.5 shrink-0 sticky top-0">
                      <p className="flex items-center gap-1.5">
                        <span className="font-heading font-bold text-[12px] text-[#0F172A] truncate">{col.label}</span>
                        <span className="text-[10px] font-bold text-neutral-400" data-testid={`prospects-count-${col.id}`}>{byCol[col.id].list.length}</span>
                      </p>
                      <p className="text-[10px] font-semibold text-neutral-500" data-testid={`prospects-total-${col.id}`}>{fmtEuro(byCol[col.id].total)}</p>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-2">
                      {byCol[col.id].list.map((p) => (
                        <ProspectCard key={p.id} p={p} onOpen={setSelectedId} onEdit={setEditFor}
                          onLost={setLossFor} onDelete={deleteProspect} />
                      ))}
                      <button onClick={() => setCreating(true)} data-testid={`prospects-add-${col.id}`}
                        className="w-full text-[10px] font-semibold text-neutral-400 hover:text-neutral-700 border border-dashed border-neutral-300/70 rounded-lg py-1.5 transition-colors">
                        + Ajouter un prospect
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-auto px-2 pb-2" data-testid="prospects-list">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
                    <th className="px-2 py-2 font-semibold">Entreprise</th><th className="px-2 py-2 font-semibold">Contact</th>
                    <th className="px-2 py-2 font-semibold">Service</th><th className="px-2 py-2 font-semibold text-right">Valeur</th>
                    <th className="px-2 py-2 font-semibold">Étape</th><th className="px-2 py-2 font-semibold">Prochaine action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const st = statusOf(p.status);
                    return (
                      <tr key={p.id} onClick={() => setSelectedId(p.id)} data-testid={`prospect-row-${p.id}`}
                        className="border-b border-neutral-50 last:border-0 hover:bg-neutral-50/70 cursor-pointer transition-colors">
                        <td className="px-2 py-2">
                          <span className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(p.name)}`}>{initialsOf(p.name)}</span>
                            <span className="text-xs font-semibold text-[#0F172A]">{p.name}</span>
                            {p.ai && <span className={`text-[9px] font-bold ${PRIO_DOT[p.ai.priorite] || "text-neutral-400"}`}>✨ {p.ai.score}</span>}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-xs text-neutral-500">{p.contact_name || "—"}</td>
                        <td className="px-2 py-2 text-xs text-neutral-500 truncate max-w-40">{p.service || "—"}</td>
                        <td className="px-2 py-2 text-xs font-semibold text-right">{valLabel(p) || "—"}</td>
                        <td className="px-2 py-2"><span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${st.chip}`}>{st.label}</span></td>
                        <td className="px-2 py-2 text-[11px] text-neutral-500">{p.next_action?.date ? `${actionLabel(p.next_action.type)} · ${frDate(p.next_action.date)}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && <p className="text-sm text-neutral-400 text-center py-8" data-testid="prospects-no-match">Aucun prospect ne correspond aux filtres.</p>}
            </div>
          )}
        </div>

        {/* Colonne droite */}
        <div className="min-h-0 overflow-y-auto space-y-3 pr-0.5" data-testid="prospects-sidebar">
          {/* Suivis du jour */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid="prospects-suivis">
            <div className="flex items-center gap-2 mb-2">
              <CalendarCheck className="w-4 h-4 text-neutral-400" />
              <h3 className="font-heading font-bold text-sm flex-1">Suivis du jour</h3>
              <span className="text-[10px] font-bold text-neutral-400">{suivisJour.filter((t) => t.status !== "finalise").length}</span>
            </div>
            {suivisJour.length === 0 && <p className="text-[11px] text-neutral-400 py-1">Aucun suivi prévu aujourd'hui.</p>}
            {suivisJour.map((t) => {
              const done = t.status === "finalise";
              return (
                <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-neutral-50 last:border-0" data-testid={`suivi-${t.id}`}>
                  <button onClick={() => completeTask(t)} data-testid={`suivi-check-${t.id}`}
                    className={`w-4 h-4 rounded-full border-2 shrink-0 transition-colors ${done ? "bg-emerald-500 border-emerald-500" : "border-neutral-300 hover:border-[#3B82F6]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] font-semibold leading-snug ${done ? "line-through text-neutral-300" : "text-[#0F172A]"}`}>
                      {t.title.replace("[Prospect] ", "").split(" — ")[1] || t.title}
                    </p>
                    <p className="text-[9px] text-neutral-400 truncate">{t.title.replace("[Prospect] ", "").split(" — ")[0]}</p>
                  </div>
                  {t.note && <span className="text-[9px] text-neutral-400 shrink-0">{t.note}</span>}
                </div>
              );
            })}
          </div>

          {/* Prochains rendez-vous */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid="prospects-rdv">
            <div className="flex items-center gap-2 mb-2">
              <CalendarRange className="w-4 h-4 text-neutral-400" />
              <h3 className="font-heading font-bold text-sm flex-1">Prochains rendez-vous</h3>
              <button onClick={() => navigate("/rdv")} className="text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="prospects-rdv-all">
                Voir l'agenda ›
              </button>
            </div>
            {prochainsRdv.length === 0 && <p className="text-[11px] text-neutral-400 py-1">Aucun RDV prospect à venir.</p>}
            {prochainsRdv.map((a) => (
              <button key={a.id} onClick={() => navigate("/rdv")} data-testid={`prospects-rdv-${a.id}`}
                className="w-full flex items-center gap-2.5 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 -mx-1 px-1 rounded-lg transition-colors text-left">
                <span className="text-[10px] font-bold text-[#1D4ED8] w-14 shrink-0">
                  {frDate(a.date)}<br />{a.start_min != null ? `${String(Math.floor(a.start_min / 60)).padStart(2, "0")}:${String(a.start_min % 60).padStart(2, "0")}` : ""}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{a.title}</span>
                  <span className="block text-[9px] text-neutral-400">{a.duration_minutes ? `${a.duration_minutes} min` : ""}{a.kind === "visio" ? " · Visio" : ""}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Prospects récents */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5" data-testid="prospects-recents">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-4 h-4 text-neutral-400" />
              <h3 className="font-heading font-bold text-sm flex-1">Prospects récents</h3>
              <button onClick={() => { setSearch(""); setRespFilter(""); setTagFilter(""); setAiFilter(""); }}
                className="text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="prospects-recents-all">
                Voir tous ›
              </button>
            </div>
            {recents.map((p) => (
              <button key={p.id} onClick={() => setSelectedId(p.id)} data-testid={`prospect-recent-${p.id}`}
                className="w-full flex items-center gap-2.5 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 -mx-1 px-1 rounded-lg transition-colors text-left">
                <span className={`w-7 h-7 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ${avatarCls(p.name)}`}>{initialsOf(p.name)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{p.name}</span>
                  <span className="block text-[9px] text-neutral-400">Ajouté le {frDate((p.created_at || "").slice(0, 10))}</span>
                </span>
                <span className="text-[11px] font-bold text-[#0F172A] shrink-0">{valLabel(p) || ""}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {creating && <ProspectModal prospect={null} onClose={() => setCreating(false)} onSaved={load} onCreateRdv={() => {}} />}
      {editFor && (
        <ProspectModal key={editFor.id} prospect={editFor} onClose={() => setEditFor(null)} onSaved={load}
          onCreateRdv={(p) => { setEditFor(null); setRdvFor(p); }} />
      )}
      {rdvFor && (
        <RdvModal prospect={rdvFor} rdv={null}
          onClose={() => setRdvFor(null)}
          onSaved={() => { setRdvFor(null); load(); }} />
      )}
      {lossFor && (
        <LossDialog prospect={lossFor} onClose={() => setLossFor(null)}
          onConfirm={(reason) => { applyStatus(lossFor, "perdu", reason); setLossFor(null); }} />
      )}
      {newClient && <NewClientDialog onClose={() => setNewClient(false)} />}
      {selected && (
        <ProspectPanel key={selected.id} prospect={selected} onClose={() => setSelectedId(null)} onChanged={load}
          onEdit={(p) => { setSelectedId(null); setEditFor(p); }}
          onCreateRdv={(p) => setRdvFor(p)}
          onLost={(p) => { setLossFor(p); }} />
      )}
      {agentOpen && <AgentIaPanel onClose={() => setAgentOpen(false)} onChanged={load} />}
    </div>
  );
}
